/**
 * Payment API — subscriptions, payments, cards, gifts, referral, pricing.
 * Uses Edge Function endpoints for Payme/Click operations.
 * Uses Supabase REST for direct DB reads (RLS protected).
 */

import { supabase } from '@turnifly/shared/api'

const EDGE_BASE = import.meta.env.VITE_SUPABASE_URL + '/functions/v1/api/v1'

function getAccessToken(): string | null {
  return localStorage.getItem('turnifly_access_token')
}

async function edgeFetch<T>(path: string, body?: unknown): Promise<T> {
  const token = getAccessToken()
  const resp = await fetch(`${EDGE_BASE}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    let msg = data.error ?? `Request failed: ${resp.status}`
    if (data.message) {
      const m = data.message
      msg = typeof m === 'object'
        ? (m.uz ?? m.en ?? m.ru ?? JSON.stringify(m))
        : String(m)
    }
    if (data.code !== undefined) msg = `[${data.code}] ${msg}`
    throw new Error(msg)
  }
  return data as T
}

// ── SUBSCRIPTION ────────────────────────────────────────────

export interface SubscriptionData {
  id: string
  plan: string
  payment_method: string
  status: 'active' | 'expired' | 'cancelled' | 'past_due' | 'grace_period'
  starts_at: string
  expires_at: string
  auto_renew: boolean
  provider: string
  card_id: string | null
  created_at: string
}

export async function fetchActiveSubscription(
  userId: string,
): Promise<SubscriptionData | null> {
  const { data } = await supabase
    .from('subscriptions')
    .select('id, plan, payment_method, status, starts_at, expires_at, auto_renew, provider, card_id, created_at')
    .eq('user_id', userId)
    .in('status', ['active', 'past_due', 'grace_period'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
    .throwOnError()
  return data as SubscriptionData | null
}

// ── PRICING ─────────────────────────────────────────────────

export interface PricingItem {
  id: string
  plan: string
  period: string
  currency: string
  amount: number
  country: string
  is_active: boolean
}

export async function fetchPricing(): Promise<PricingItem[]> {
  const { data } = await supabase
    .from('pricing')
    .select('id, plan, period, currency, amount, country, is_active')
    .eq('is_active', true)
    .order('amount')
    .throwOnError()
  return (data ?? []) as PricingItem[]
}

// ── CARDS ───────────────────────────────────────────────────

export interface UserCard {
  id: string
  provider: 'payme' | 'click'
  card_number_masked: string
  card_expire: string | null
  card_brand: string | null
  is_default: boolean
  created_at: string
}

export async function fetchUserCards(): Promise<UserCard[]> {
  const resp = await edgeFetch<{ cards: UserCard[] }>('/payments/cards')
  return resp.cards
}

export interface CardCreateResult {
  card_id: string
  token: string
  phone: string
  wait: number
  card_number_masked: string
  card_brand: string
}

export async function createCard(
  cardNumber: string,
  cardExpire: string,
): Promise<CardCreateResult> {
  return edgeFetch<CardCreateResult>('/payments/cards/create', {
    card_number: cardNumber,
    card_expire: cardExpire,
  })
}

export async function verifyCard(
  cardId: string,
  token: string,
  code: string,
): Promise<{ success: boolean; is_default: boolean }> {
  return edgeFetch('/payments/cards/verify', { card_id: cardId, token, code })
}

export async function removeCard(cardId: string): Promise<{ success: boolean }> {
  return edgeFetch('/payments/cards/remove', { card_id: cardId })
}

// ── PAYMENT CALCULATION ────────────────────────────────────

export interface PaymentCalculation {
  base_amount: number
  discount_percent: number
  discount_amount: number
  referral_balance: number
  referral_balance_used: number
  final_amount: number
  promo_type: string | null
  error?: string
  promo_valid: {
    valid: boolean
    type?: string
    error?: string
    plan?: string
    gift_id?: string
    discount_percent?: number
    applies_to?: string
  } | null
}

export async function calculatePayment(
  plan: string,
  period: string,
  promoCode?: string,
): Promise<PaymentCalculation> {
  return edgeFetch<PaymentCalculation>('/payments/calculate', {
    plan,
    period,
    promo_code: promoCode,
  })
}

// ── PROMO VALIDATION ───────────────────────────────────────

export interface PromoValidation {
  valid: boolean
  type?: 'referral' | 'gift'
  error?: string
  discount_percent?: number
  applies_to?: string
  plan?: string
  gift_id?: string
  remaining?: number
  retry_after_seconds?: number
}

export async function validatePromo(code: string): Promise<PromoValidation> {
  return edgeFetch<PromoValidation>('/payments/validate-promo', { code })
}

// ── CHECKOUT (ONE-TIME) ────────────────────────────────────

export interface CheckoutResult {
  type: 'redirect' | 'free'
  checkout_url?: string
  payment_id?: string
  amount?: number
  referral_balance_used?: number
  success?: boolean
}

export async function createCheckout(params: {
  plan: string
  period: string
  provider: 'payme' | 'click'
  promo_code?: string
  type?: 'premium' | 'gift' | 'challenge'
  gift_quantity?: number
  gift_plan?: string
  gift_message?: string
  challenge_id?: string
}): Promise<CheckoutResult> {
  return edgeFetch<CheckoutResult>('/payments/create-checkout', params)
}

// ── SUBSCRIBE (CARD CHARGE) ────────────────────────────────

export interface SubscribeResult {
  success: boolean
  subscription?: {
    subscription_id: string
    expires_at: string
  }
}

export async function subscribeWithCard(params: {
  plan: string
  period: string
  card_id: string
  promo_code?: string
}): Promise<SubscribeResult> {
  return edgeFetch<SubscribeResult>('/payments/subscribe', params)
}

// ── GIFT ────────────────────────────────────────────────────

export interface GiftSubscriptionItem {
  id: string
  promo_code: string
  plan: string
  message: string | null
  status: 'created' | 'redeemed' | 'expired'
  usage_limit: number
  usage_count: number
  expires_at: string
  created_at: string
}

export async function fetchMyGifts(userId: string): Promise<GiftSubscriptionItem[]> {
  const { data } = await supabase
    .from('gift_subscriptions')
    .select('id, promo_code, plan, message, status, usage_limit, usage_count, expires_at, created_at')
    .eq('buyer_id', userId)
    .order('created_at', { ascending: false })
    .throwOnError()
  return (data ?? []) as GiftSubscriptionItem[]
}

export interface CreateGiftResult {
  checkout_url: string
  payment_id: string
  gift_id: string
  promo_code: string
  amount: number
  quantity: number
}

export async function createGift(params: {
  plan: string
  quantity: number
  message?: string
  provider: 'payme' | 'click'
}): Promise<CreateGiftResult> {
  return edgeFetch<CreateGiftResult>('/payments/create-gift', params)
}

export interface RedeemGiftResult {
  success: boolean
  error?: string
  subscription_id?: string
  plan?: string
  expires_at?: string
}

export async function redeemGiftCode(code: string): Promise<RedeemGiftResult> {
  return edgeFetch<RedeemGiftResult>('/payments/redeem-gift', { code })
}

// ── PAYMENTS HISTORY ────────────────────────────────────────

export interface PaymentHistoryItem {
  id: string
  type: string
  amount: number
  currency: string
  status: string
  provider: string
  description: string | null
  created_at: string
}

export async function fetchPaymentHistory(
  userId: string,
  limit = 50,
): Promise<PaymentHistoryItem[]> {
  const { data } = await supabase
    .from('payments')
    .select('id, type, amount, currency, status, provider, description, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
    .throwOnError()
  return (data ?? []) as PaymentHistoryItem[]
}

// ── REFERRAL ────────────────────────────────────────────────

export interface ReferralBalance {
  balance: number
  total_earned: number
  total_withdrawn: number
}

export async function fetchReferralBalance(
  userId: string,
): Promise<ReferralBalance> {
  const { data } = await supabase
    .from('referral_balances')
    .select('balance, total_earned, total_withdrawn')
    .eq('user_id', userId)
    .maybeSingle()
    .throwOnError()
  return (data ?? { balance: 0, total_earned: 0, total_withdrawn: 0 }) as ReferralBalance
}

export interface ReferralFriend {
  id: string
  referred_id: string
  status: string
  bonus_amount: number
  created_at: string
}

export async function fetchReferralFriends(
  userId: string,
): Promise<ReferralFriend[]> {
  const { data } = await supabase
    .from('referrals')
    .select('id, referred_id, status, bonus_amount, created_at')
    .eq('referrer_id', userId)
    .order('created_at', { ascending: false })
    .throwOnError()
  return (data ?? []) as ReferralFriend[]
}

export interface WithdrawalResult {
  success: boolean
  withdrawal_id?: string
  amount?: number
  error?: string
}

export async function requestWithdrawal(
  cardNumber: string,
  cardHolder: string,
): Promise<WithdrawalResult> {
  return edgeFetch<WithdrawalResult>('/payments/request-withdrawal', {
    card_number: cardNumber,
    card_holder: cardHolder,
  })
}

// ── PAYMENT STATUS (polling) ────────────────────────────────

export async function fetchPaymentStatus(
  paymentId: string,
): Promise<{ status: string }> {
  const { data } = await supabase
    .from('payments')
    .select('status')
    .eq('id', paymentId)
    .single()
    .throwOnError()
  return data as { status: string }
}
